'use client';

import { Auth0Context, initialContext } from '@auth0/auth0-react';
import { useCallback, useContext } from 'react';

import {
    type Auth0SessionLogoutOptions,
    clearLocalAuth0SessionState,
    resolveInactivityReauthReturnTo,
    SESSION_EVALUATED_DOM_EVENT,
} from '@libs/core';

import { UserSessionProviderContext } from '../providers/UserSessionProvider';
import { buildAuth0ReauthSignInUrl } from '../utils/getAuth0ReturnTo';

type Auth0SessionLogoutFn = (options?: Auth0SessionLogoutOptions) => Promise<void>;

/** Long enough for `/v2/logout` to answer, short enough to clean up a blocked request. */
const BACKGROUND_LOGOUT_TIMEOUT_MS = 10_000;

/**
 * Requests Auth0 `/v2/logout` in a hidden iframe instead of navigating to it, so the
 * SSO session ends while the user keeps reading the page they are on.
 *
 * Returns as soon as the request is under way: session termination is best effort, and
 * nothing the user is doing should wait for it. The iframe is removed once Auth0 answers,
 * or on timeout when a browser that blocks the Auth0 cookie in third-party contexts never
 * lets the request through. A later top-level authorize can still send the SSO cookie,
 * so sign-in after inactivity must require credentials (`reauth=1` or `isLastActiveExpired()`).
 *
 * Without a `returnTo`, Auth0 redirects to the first Allowed Logout URL (the storefront
 * origin). An empty `sandbox` keeps that landing page from sharing cookies, `localStorage`,
 * or script access with the top window even though the URL is same-origin.
 */
function startBackgroundAuth0Logout(logoutUrl: string): void {
    const iframe = document.createElement('iframe');
    iframe.title = 'Auth0 logout';
    iframe.setAttribute('aria-hidden', 'true');
    iframe.setAttribute('sandbox', '');
    iframe.style.display = 'none';

    let cleanupTimeoutId: ReturnType<typeof setTimeout>;
    const removeIframe = () => {
        clearTimeout(cleanupTimeoutId);
        iframe.remove();
    };

    // Auth0 is cross-origin, so `load` is the only available signal; it says the request
    // completed, not whether the session was cleared.
    iframe.addEventListener('load', removeIframe, { once: true });
    cleanupTimeoutId = setTimeout(removeIframe, BACKGROUND_LOGOUT_TIMEOUT_MS);

    iframe.src = logoutUrl;
    document.body.append(iframe);
}

/**
 * Auth0 session termination for inactivity and other demand-driven expiry checks
 * wired through `evaluateSession`.
 *
 * Clears local session state and always ends the Auth0 server session, because a
 * local-only logout leaves the SSO session alive and the SDK silently restores it on the
 * next page load, which would hand the user a fresh 30-minute window without ever asking
 * for credentials.
 *
 * Redirects through `/v2/logout` to `/sign-in`, carrying the page to resume, so the user
 * signs in again wherever they were. Callers that drive their own sign-in flow on the
 * current page (`skipRedirect`) instead get a background logout that ends the Auth0
 * session without navigating.
 */
export function useAuth0SessionLogout(): Auth0SessionLogoutFn {
    const { logout } = useContext(Auth0Context);
    const userSessionContext = useContext(UserSessionProviderContext);
    const hasProvider = logout !== initialContext.logout;
    // `setUser` is stable; the full context object is not (it changes when user is cleared).
    const setUser = userSessionContext?.setUser;

    return useCallback(
        async ({ callback, skipRedirect, redirectUrl }: Auth0SessionLogoutOptions = {}) => {
            if (!hasProvider) {
                return;
            }

            setUser?.(undefined);
            clearLocalAuth0SessionState(callback);
            globalThis.window?.dispatchEvent(new CustomEvent(SESSION_EVALUATED_DOM_EVENT));

            if (globalThis.window === undefined) {
                await logout({ openUrl: false });
                return;
            }

            // Restricted pages send the user through sign-in with a resume destination;
            // everywhere else ends the Auth0 session in the background without leaving the page.
            const reauthDestination = resolveInactivityReauthReturnTo({
                skipRedirect,
                redirectUrl,
                onlyWhenRestricted: true,
            });

            if (reauthDestination) {
                // Current origin, not NEXT_PUBLIC_ORIGIN, so the user returns to the host
                // they are actually browsing.
                await logout({
                    logoutParams: {
                        returnTo: buildAuth0ReauthSignInUrl(globalThis.location.origin, reauthDestination),
                    },
                });
                return;
            }

            // The caller keeps the user on the current page to run its own sign-in flow, so
            // the Auth0 session has to end without navigating the top window.
            await logout({
                openUrl: startBackgroundAuth0Logout,
            });
        },
        [hasProvider, logout, setUser]
    );
}
